import type { AnalysisTest } from '@/globals/typescript/models/AnalysisTest'
import type { ReportDescriptionItem } from '@/stores/reportDescriptionItemStore'
import type { TAnalysisResultValue } from '@team-milva/lab-models/resources'

/**
 * A saved draft value for a multi-select test that uses what the test's setup allows,
 * so every layout of the "Multivalg: alle varianter" analysis opens with something to
 * edit: up to three rows taken one per pooled Collection in turn (so each row's source
 * shows), a suffix on the first and third row where the test has suffix Collections
 * (and none on the second, for "Intet suffiks"), answers by `answerMode`
 * (none → no answer, optional → one row left empty, required → every row answered), a
 * number on every row except the last, which takes the test's inactive answer/override
 * when it has one (a value saved before its answer was deactivated), else an active
 * override, else a number. The sum is the numeric rows plus the unit, when the test has one.
 */
export function buildFilledMultiSelectValue(
  analysisTest: AnalysisTest,
  allReportDescriptionItems: ReportDescriptionItem[],
  unitLabel: string
): TAnalysisResultValue | null {
  if (
    analysisTest.type !== 'multi-select-text' &&
    analysisTest.type !== 'multi-select-number'
  ) {
    return null
  }

  const byCollection = analysisTest.reportDescriptionIDs.map((collectionID) =>
    allReportDescriptionItems.filter((x) => x.reportDescriptionID === collectionID)
  )
  const rows: ReportDescriptionItem[] = []
  for (let position = 0; rows.length < 3; position++) {
    const nextRound = byCollection
      .map((items) => items[position])
      .filter((x): x is ReportDescriptionItem => x !== undefined)
    if (nextRound.length === 0) break
    rows.push(...nextRound.slice(0, 3 - rows.length))
  }

  const suffixPool = allReportDescriptionItems.filter((x) =>
    (analysisTest.suffixReportDescriptionIDs ?? []).includes(x.reportDescriptionID)
  )
  const suffixFor = (rowIndex: number): string | null =>
    suffixPool[rowIndex === 0 ? 0 : rowIndex === 2 ? 1 : -1]?.id ?? null

  if (analysisTest.type === 'multi-select-text') {
    const active = analysisTest.answers.filter((x) => x.activeState === 'active')
    const inactive = analysisTest.answers.find((x) => x.activeState !== 'active')
    const answerFor = (rowIndex: number, isLast: boolean): string | null => {
      const mode = analysisTest.options.answerMode
      if (mode === 'none') return null
      if (isLast && inactive) return inactive.id
      if (mode === 'optional' && rowIndex === 1) return null
      return active[rowIndex % Math.max(active.length, 1)]?.id ?? null
    }
    return {
      type: 'multi-select-text',
      reportDescriptionIDs: analysisTest.reportDescriptionIDs,
      nothingToAdd: rows.length === 0,
      items: rows.map((item, i) => ({
        reportDescriptionItemID: item.id,
        answerID: answerFor(i, i === rows.length - 1),
        suffixID: suffixFor(i),
      })),
    }
  }

  const activeOverride = analysisTest.answerOverrides.find((x) => x.activeState === 'active')
  const inactiveOverride = analysisTest.answerOverrides.find((x) => x.activeState !== 'active')
  const numbers = [12, 24, 6]
  const items = rows.map((item, i) => {
    const overrideForRow =
      i === rows.length - 1 && rows.length > 1
        ? (inactiveOverride ?? activeOverride)
        : undefined
    return {
      reportDescriptionItemID: item.id,
      value: overrideForRow ? null : (numbers[i] ?? 6),
      answerOverrideID: overrideForRow?.id ?? null,
      suffixID: suffixFor(i),
    }
  })
  const sum = items.reduce((total, item) => total + (item.value ?? 0), 0)
  return {
    type: 'multi-select-number',
    reportDescriptionIDs: analysisTest.reportDescriptionIDs,
    nothingToAdd: items.length === 0,
    items,
    sumValue:
      analysisTest.options.hasSumValue && items.some((x) => x.value !== null)
        ? unitLabel
          ? `${sum} ${unitLabel}`
          : String(sum)
        : null,
  }
}
